import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {PopoverMenuComponent} from '@lib/components/popover-menu/popover-menu.component';
import {OptionGroup, Option} from '@shared/models/option';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {TitleCasePipe} from '@angular/common';
import {TimeStep, TimeUnit, TIME_UNIT_MULTIPLIERS, CUSTOM_STEP_ID, CUSTOM_STEP_LABEL} from '@features/canvas-tiles/shared/canvas-tile.constants';
import {ClickOutsideServiceDirective} from '@lib/directives/click-outside-service.directive';

/**
 * Picker component for configuring a custom time step interval
 * Allows user to specify a value and unit (seconds/minutes/hours)
 */
@Component({
  selector: 'motn-custom-step-picker',
  standalone: true,
  templateUrl: './custom-step-picker.component.html',
  styleUrls: ['./custom-step-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PopoverMenuComponent,
    TitleCasePipe,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ClickOutsideServiceDirective,
  ],
})
export class CustomStepPickerComponent {
  @Input() value: number = 1;
  @Input() unit: TimeUnit = TimeUnit.MINUTES;
  @Output() valueChange = new EventEmitter<TimeStep>();
  @Output() unitChange = new EventEmitter<TimeUnit>();
  @Output() closed$ = new EventEmitter<void>();

  unitSelectorOpen = false;

  readonly unitOptions: OptionGroup = new OptionGroup([
    { id: TimeUnit.SECONDS, name: 'Seconds' },
    { id: TimeUnit.MINUTES, name: 'Minutes' },
    { id: TimeUnit.HOURS, name: 'Hours' },
  ]);

  // Position for the picker overlay itself (relative to picker-anchor)
  readonly overlayPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  }];

  // Position for the unit dropdown (relative to unit button)
  readonly unitDropdownPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  }];

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = parseInt(input.value, 10);

    // Only update if it's a valid number
    if (!isNaN(newValue) && newValue > 0) {
      this.value = newValue;
      this.emitChange();
    }
  }

  onInputBlur(): void {
    // If value is invalid or empty, reset to 1
    if (!this.value || this.value < 1) {
      this.value = 1;
      this.emitChange();
    }
  }

  onUnitChange(option: Option): void {
    const newUnit = option.id as TimeUnit;

    this.unit = newUnit;
    this.unitSelectorOpen = false;

    // Emit the unit change so parent can sync
    this.unitChange.emit(this.unit);
    this.emitChange();
  }

  private emitChange(): void {
    this.valueChange.emit({
      id: CUSTOM_STEP_ID,
      label: CUSTOM_STEP_LABEL,
      seconds: this.value * TIME_UNIT_MULTIPLIERS[this.unit],
    });
  }
}
